import { isPlatformBrowser } from '@angular/common';
import {
  Directive,
  ElementRef,
  inject,
  Input,
  OnChanges,
  PLATFORM_ID,
} from '@angular/core';

/**
 * Applies custom padding and a header divider to the goab-drawer.
 *
 * The drawer header padding, header border, and content vertical padding are
 * hard-coded in the goa-drawer shadow DOM with no overridable CSS token, so they
 * are set directly on the shadow root elements. Browser-only (SSR-safe) and
 * retries while the shadow content renders.
 */
@Directive({
  selector: '[customDrawer]',
  standalone: true,
})
export class CustomDrawerDirective implements OnChanges {
  @Input() open = false;
  @Input() headerPadding = '15px 25px 10px 25px';
  @Input() headerBorderBottom =
    'var(--goa-border-width-s) solid var(--goa-color-greyscale-200)';
  @Input() contentPaddingTop = '10px';
  @Input() contentPaddingBottom = '0';
  /** Left/right spacing of a bottom drawer (maps to --goa-drawer-offset). */
  @Input() offset?: string;
  /** Max size passed directly to goa-drawer, allowing CSS units unsupported by the Angular wrapper. */
  @Input() customMaxSize =
    'calc(100dvh - var(--goa-drawer-offset, 0px) - var(--goa-drawer-offset, 0px))';
  /** Top border on the actions footer (v2 bottom drawer removes it by default). */
  @Input() actionsBorderTop?: string;
  /** Hide the (empty) actions footer section and its padding. */
  @Input() hideActions = false;

  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly platformId = inject(PLATFORM_ID);

  ngOnChanges(): void {
    if (this.open && isPlatformBrowser(this.platformId)) {
      this.styleDrawer();
    }
  }

  private styleDrawer(retries = 5): void {
    const drawer =
      this.el.nativeElement.querySelector<HTMLElement>('goa-drawer');
    const shadow = drawer?.shadowRoot;
    const header = shadow?.querySelector<HTMLElement>('.header');
    const content = shadow?.querySelector<HTMLElement>('.drawer-content');
    const scrollContent = shadow?.querySelector<HTMLElement>('.scroll-content');
    const actions = shadow?.querySelector<HTMLElement>('.drawer-actions');
    if (this.offset != null) {
      drawer?.style.setProperty('--goa-drawer-offset', this.offset);
    }
    drawer?.setAttribute('maxsize', this.customMaxSize);
    if (this.actionsBorderTop != null && actions) {
      actions.style.setProperty(
        'border-top',
        this.actionsBorderTop,
        'important'
      );
    }
    if (this.hideActions && actions) {
      actions.style.setProperty('display', 'none', 'important');
    }
    if (header && content) {
      header.style.padding = this.headerPadding;
      header.style.borderBottom = this.headerBorderBottom;
      content.style.paddingTop = this.contentPaddingTop;
      content.style.paddingBottom = this.contentPaddingBottom;
      if (scrollContent) {
        scrollContent.style.paddingTop = this.contentPaddingTop;
        scrollContent.style.paddingBottom = this.contentPaddingBottom;
      }
      this.injectPersistentStyles(shadow);
    } else if (drawer && retries > 0) {
      requestAnimationFrame(() => this.styleDrawer(retries - 1));
    }
  }

  /**
   * The inline styles above are reset whenever the drawer re-renders its shadow
   * DOM internally, so also inject a stylesheet that persists across re-renders.
   */
  private injectPersistentStyles(shadow: ShadowRoot | null | undefined): void {
    if (!shadow || typeof shadow.appendChild !== 'function') {
      return;
    }

    const rules = [
      `.header { padding: ${this.headerPadding} !important; border-bottom: ${this.headerBorderBottom} !important; }`,
      `.drawer-content, .scroll-content { padding-top: ${this.contentPaddingTop} !important; padding-bottom: ${this.contentPaddingBottom} !important; }`,
    ];
    if (this.hideActions) {
      rules.push('.drawer-actions { display: none !important; }');
    }
    if (this.actionsBorderTop != null) {
      rules.push(
        `.drawer-actions { border-top: ${this.actionsBorderTop} !important; }`
      );
    }

    let style = shadow.querySelector<HTMLStyleElement>(
      'style#custom-drawer-styles'
    );
    if (!style) {
      style = document.createElement('style');
      style.id = 'custom-drawer-styles';
      shadow.appendChild(style);
    }
    style.textContent = rules.join('\n');
  }
}
